import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ApiErrorException } from '@/api/client';
import { getSwoftAvailability } from '@/api/endpoints/swoftAvailability';
import { currentLocale } from '@/i18n/locale';
import {
  availabilityWindow,
  dayKey,
  firstDayWithSlots,
  formatPickerDay,
  formatSlotTime,
  groupSlotsByDay,
  includesOutlook,
  pickerDays,
  type AvailabilitySlot,
} from '@/services/dialer/slotPicker';
import { isSlotConfigurationFailure } from '@/services/dialer/bookedMeeting';

/**
 * The free slots of the person who holds this client's meetings, read from
 * the client's Swoft CRM over the next 14 days, one day at a time.
 *
 * Picking a slot hands it to the form, which records the meeting at that
 * date and time exactly as if it had been typed. The picker never books
 * anything itself.
 */

export interface SwoftSlotPickerProps {
  customerId: string;
  durationMinutes: number;
  selectedStart: string | null;
  onSelect: (slot: AvailabilitySlot) => void;
  /**
   * Le calendrier Swoft ne peut pas repondre pour ce client : ni connexion,
   * ni personne qui tienne ses rendez-vous. Le formulaire reprend la main et
   * propose le lien de prise de rendez-vous ; le picker n'affiche rien.
   */
  onUnavailable?: (code: string) => void;
}

export default function SwoftSlotPicker({ customerId, durationMinutes, selectedStart, onSelect, onUnavailable }: SwoftSlotPickerProps) {
  const { t } = useTranslation('dialer');
  // The window is fixed when the picker opens, so the query key stays stable
  // across renders instead of refetching every time "now" moves.
  const [opened] = useState(() => new Date());
  const days = useMemo(() => pickerDays(opened), [opened]);
  const range = useMemo(() => availabilityWindow(opened), [opened]);

  const query = useQuery({
    queryKey: ['swoft-availability', customerId, durationMinutes, range.from.toISOString()],
    queryFn: () => getSwoftAvailability({ customerId, from: range.from, to: range.to, durationMinutes }),
    retry: false,
    staleTime: 60_000,
  });

  const byDay = useMemo(() => groupSlotsByDay(query.data?.slots ?? [], opened), [query.data, opened]);
  const [dayIndex, setDayIndex] = useState(0);

  // Land on the first day that has something to offer once the slots arrive.
  useEffect(() => {
    if (query.data) setDayIndex(firstDayWithSlots(days, byDay));
  }, [query.data, days, byDay]);

  // Un refus de configuration n'est pas une erreur a afficher : c'est au
  // formulaire de basculer sur le lien de prise de rendez-vous.
  const code = errorCode(query.error);
  const configurationManquante = query.isError && isSlotConfigurationFailure(code);
  useEffect(() => {
    if (configurationManquante) onUnavailable?.(code);
  }, [configurationManquante, code, onUnavailable]);
  if (configurationManquante) return null;

  const day = days[dayIndex] ?? opened;
  const daySlots = byDay.get(dayKey(day)) ?? [];

  return (
    <div className="border border-border rounded-xl overflow-hidden bg-card">
      <div className="flex items-center justify-between gap-2 px-3 py-2 surface-1 border-b border-border/50">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          disabled={dayIndex === 0}
          onClick={() => setDayIndex((i) => Math.max(0, i - 1))}
          aria-label={t('bookedMeeting.slots.previousDay')}
        >
          <ChevronLeft className="w-4 h-4 text-muted-foreground" />
        </Button>
        <div className="flex flex-col items-center min-w-0">
          <span className="text-sm font-semibold text-foreground first-letter:uppercase truncate">
            {formatPickerDay(day, currentLocale())}
          </span>
          {query.data && includesOutlook(query.data.sources) && (
            <Badge variant="secondary" className="mt-0.5 text-[10px] px-1.5 py-0">{t('bookedMeeting.slots.withOutlook')}</Badge>
          )}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          disabled={dayIndex >= days.length - 1}
          onClick={() => setDayIndex((i) => Math.min(days.length - 1, i + 1))}
          aria-label={t('bookedMeeting.slots.nextDay')}
        >
          <ChevronRight className="w-4 h-4 text-muted-foreground" />
        </Button>
      </div>

      <div className="p-3 min-h-[88px]">
        {query.isLoading && (
          <div className="flex items-center justify-center gap-2 py-5 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" />
            {t('bookedMeeting.slots.loading')}
          </div>
        )}

        {query.isError && (
          <div className="flex flex-col items-center gap-2 py-3 text-center">
            <p className="text-sm text-destructive">{errorMessage(query.error, t)}</p>
            <Button type="button" variant="outline" size="sm" onClick={() => query.refetch()}>
              {t('bookedMeeting.slots.retry')}
            </Button>
          </div>
        )}

        {query.data && (
          <>
            {query.data.externalCalendarError && (
              <p className="flex items-start gap-1.5 mb-2 text-xs text-amber-600">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                {t('bookedMeeting.slots.outlookError', { message: query.data.externalCalendarError })}
              </p>
            )}
            {daySlots.length === 0 ? (
              <p className="py-5 text-center text-sm text-muted-foreground">{t('bookedMeeting.slots.empty')}</p>
            ) : (
              <div className="grid grid-cols-4 gap-2">
                {daySlots.map((slot) => {
                  const selected = slot.start === selectedStart;
                  return (
                    <Button
                      key={slot.start}
                      type="button"
                      size="sm"
                      variant={selected ? 'default' : 'outline'}
                      aria-pressed={selected}
                      onClick={() => onSelect(slot)}
                    >
                      {formatSlotTime(slot.start)}
                    </Button>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function errorCode(err: unknown): string {
  return err instanceof ApiErrorException ? err.code : '';
}

/** Les refus de configuration ne passent jamais par ici : ils sortent du
 *  picker par onUnavailable. Reste ce qui peut se reessayer. */
function errorMessage(err: unknown, t: (key: string, options?: Record<string, unknown>) => string): string {
  const message = (err as { message?: string } | null)?.message || t('bookedMeeting.toast.unknownError');
  return t('bookedMeeting.slots.error', { message });
}
